import { useState, useRef, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Textarea, Checkbox, Dialog } from '@artosera/ui';
import { CHAMPS_PAR_GENRE, type GenreContrat, type Langue } from '@artosera/shared';
import {
  useModeleContrat,
  useEnregistrerModeleContrat,
  useReinitialiserModeleContrat,
  type CorpsModeleContrat,
} from '../../api/contrats.js';
import { Field } from '../Field.js';
import { erreurContrat } from './contratUi.js';
import styles from './ContratsPage.module.css';
export function EditeurModeleContrat({
  galerieSlug,
  genre,
  langue,
}: {
  galerieSlug: string;
  genre: GenreContrat;
  langue: Langue;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const modele = useModeleContrat(galerieSlug, genre, langue);
  if (modele.isError)
    return <p role="alert">{erreurContrat(modele.error, t('commun:erreurReseau'))}</p>;
  if (!modele.data) return <p role="status">{t('commun:chargement')}</p>;
  return (
    <Edition
      key={`${genre}:${langue}:${modele.data.version}`}
      galerieSlug={galerieSlug}
      genre={genre}
      langue={langue}
      initial={modele.data}
    />
  );
}
function Edition({
  galerieSlug,
  genre,
  langue,
  initial,
}: {
  galerieSlug: string;
  genre: GenreContrat;
  langue: Langue;
  initial: CorpsModeleContrat;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const label = (s: string) => t(`regie:contrats.modele.${s}`);
  const [corps, setCorps] = useState<CorpsModeleContrat>(() => structuredClone(initial));
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState(false);
  const [reset, setReset] = useState(false);
  const sauver = useEnregistrerModeleContrat(galerieSlug);
  const retablir = useReinitialiserModeleContrat(galerieSlug);
  const actif = useRef<{
    element: HTMLInputElement | HTMLTextAreaElement;
    modifier: (v: string) => void;
  } | null>(null);
  const texte = (cle: 'titre' | 'preambule' | 'fait', v: string) =>
    setCorps((c) => ({ ...c, [cle]: v, valideParGalerie: false }));
  const article = (i: number, cle: 'titre' | 'texte', v: string) =>
    setCorps((c) => ({
      ...c,
      valideParGalerie: false,
      articles: c.articles.map((a, j) => (j === i ? { ...a, [cle]: v } : a)),
    }));
  function inserer(champ: string) {
    const a = actif.current;
    if (!a) return;
    const e = a.element;
    const debut = e.selectionStart ?? e.value.length;
    const fin = e.selectionEnd ?? debut;
    const token = `{{${champ}}}`;
    a.modifier(e.value.slice(0, debut) + token + e.value.slice(fin));
    requestAnimationFrame(() => {
      e.focus();
      e.setSelectionRange(debut + token.length, debut + token.length);
    });
  }
  async function enregistrer() {
    setErreur('');
    setSucces(false);
    try {
      await sauver.mutateAsync({
        genre,
        langue,
        corps: {
          titre: corps.titre,
          preambule: corps.preambule,
          articles: corps.articles,
          fait: corps.fait,
          valideParGalerie: corps.valideParGalerie,
        },
      });
      setSucces(true);
    } catch (e) {
      setErreur(erreurContrat(e, t('commun:erreurReseau')));
    }
  }
  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void enregistrer();
      }}
    >
      <p className={styles.banniere}>{t('regie:contrats.banniereNeutre')}</p>
      {(['titre', 'preambule'] as const).map((cle) => (
        <Field key={cle} htmlFor={`modele-${cle}`} label={label(cle)}>
          <Textarea
            id={`modele-${cle}`}
            value={corps[cle]}
            onChange={(e) => texte(cle, e.target.value)}
            onFocus={(e) => {
              actif.current = { element: e.currentTarget, modifier: (v) => texte(cle, v) };
            }}
          />
        </Field>
      ))}
      <fieldset className={styles.form}>
        <legend>{label('articles')}</legend>
        {corps.articles.map((a, i) => (
          <div key={i} className={styles.article}>
            <Field
              htmlFor={`article-titre-${i}`}
              label={`${label('article')} ${i + 1} — ${label('titre')}`}
            >
              <Input
                id={`article-titre-${i}`}
                value={a.titre}
                onChange={(e) => article(i, 'titre', e.target.value)}
                onFocus={(e) => {
                  actif.current = {
                    element: e.currentTarget,
                    modifier: (v) => article(i, 'titre', v),
                  };
                }}
              />
            </Field>
            <Field htmlFor={`article-texte-${i}`} label={`${label('article')} ${i + 1}`}>
              <Textarea
                id={`article-texte-${i}`}
                value={a.texte}
                onChange={(e) => article(i, 'texte', e.target.value)}
                onFocus={(e) => {
                  actif.current = {
                    element: e.currentTarget,
                    modifier: (v) => article(i, 'texte', v),
                  };
                }}
              />
            </Field>
            <div className={styles.actions}>
              {([-1, 1] as const).map((d) => (
                <Button
                  type="button"
                  key={d}
                  disabled={i + d < 0 || i + d >= corps.articles.length}
                  onClick={() =>
                    setCorps((c) => {
                      const arr = [...c.articles];
                      const a = arr[i];
                      const b = arr[i + d];
                      if (a && b) {
                        arr[i] = b;
                        arr[i + d] = a;
                      }
                      return { ...c, articles: arr, valideParGalerie: false };
                    })
                  }
                >
                  {label(d === -1 ? 'monter' : 'descendre')}
                </Button>
              ))}
              <Button
                type="button"
                disabled={corps.articles.length === 1}
                onClick={() =>
                  setCorps((c) => ({
                    ...c,
                    valideParGalerie: false,
                    articles: c.articles.filter((_, j) => i !== j),
                  }))
                }
              >
                {label('retirer')}
              </Button>
            </div>
          </div>
        ))}
        <Button
          type="button"
          onClick={() =>
            setCorps((c) => ({
              ...c,
              valideParGalerie: false,
              articles: [...c.articles, { titre: '', texte: '' }],
            }))
          }
        >
          {label('ajouterArticle')}
        </Button>
      </fieldset>
      <Field htmlFor="modele-fait" label={label('fait')}>
        <Textarea
          id="modele-fait"
          value={corps.fait}
          onChange={(e) => texte('fait', e.target.value)}
          onFocus={(e) => {
            actif.current = { element: e.currentTarget, modifier: (v) => texte('fait', v) };
          }}
        />
      </Field>
      <fieldset>
        <legend>{label('champs')}</legend>
        <div className={styles.actions}>
          {CHAMPS_PAR_GENRE[genre].map((c) => (
            <Button type="button" key={c} onClick={() => inserer(c)}>
              <span className={styles.numero}>{c}</span>
            </Button>
          ))}
        </div>
      </fieldset>
      <Checkbox
        id="modele-valide"
        label={label('valide')}
        checked={corps.valideParGalerie}
        onCheckedChange={(v) => setCorps((c) => ({ ...c, valideParGalerie: v === true }))}
      />
      <p className={styles.aide}>{label('valideAide')}</p>
      {erreur && <p role="alert">{erreur}</p>}
      {succes && <p role="status">{label('enregistre')}</p>}
      <div className={styles.actions}>
        <Button type="submit" loading={sauver.isPending}>
          {label('enregistrer')}
        </Button>
        <Button type="button" onClick={() => setReset(true)}>
          {label('retablir')}
        </Button>
      </div>
      <Dialog
        open={reset}
        onOpenChange={setReset}
        title={label('retablir')}
        description={label('retablirConfirmation')}
        closeLabel={t('commun:actions.fermer')}
      >
        <Button
          type="button"
          loading={retablir.isPending}
          onClick={() => {
            void retablir
              .mutateAsync({ genre, langue })
              .then(() => setReset(false))
              .catch((e) => {
                setErreur(erreurContrat(e, t('commun:erreurReseau')));
                setReset(false);
              });
          }}
        >
          {label('retablir')}
        </Button>
      </Dialog>
    </form>
  );
}
